import React, { useState, useEffect } from "react";
import { Modal, Table, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { API_URL } from "../../../../constants";
import { PostServiceCaller } from "../../../../services/serviceCaller";
import { FaSpinner } from "react-icons/fa";
import moment from "moment";

const EditWfhModal = ({ show, onHide, groupedRequests, onSuccess }) => {
  const [wfhRows, setWfhRows] = useState({});
  const [submitted, setSubmit] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [dateToCancel, setDateToCancel] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (show && groupedRequests && groupedRequests.length > 0) {
      const initialRows = {};
      groupedRequests.forEach((req) => {
        const dateStr = moment(req.date || req.from_date).format("DD-MMM-YYYY");
        let mappedType = req.request_type;
        if (mappedType === "6961" || mappedType === 6961) mappedType = "WFH";
        if (mappedType === "6962" || mappedType === 6962) mappedType = "Business Travel";

        initialRows[dateStr] = {
          id: req.id,
          type: mappedType,
          reason: req.reason,
          status: req.status_name || "Pending",
        };
      });
      setWfhRows(initialRows);
      setSubmit(false);
    }
  }, [groupedRequests, show]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const requestsToUpdate = [];

    for (const date in wfhRows) {
      const row = wfhRows[date];
      if (row.status === "Cancelled") continue;
      
      if (!row.type) {
        toast.error(`Please select a request type for ${date}`);
        return;
      }
      if (!row.reason || !row.reason.trim()) {
        toast.error(`Please enter a reason for ${date}`);
        return;
      }
      
      requestsToUpdate.push({
        id: row.id,
        request_type: row.type,
        reason: row.reason,
      });
    }

    setSubmit(true);

    try {
      const response = await PostServiceCaller(
        `${API_URL}work-requests/bulk-update`,
        { requests: requestsToUpdate },
        "put"
      );

      if (response.status === 200) {
        toast.success("WFH/BT request updated successfully!");
        onSuccess();
        onHide();
      } else {
        toast.error("Failed to update request.");
      }
    } catch (error) {
      console.error("Error updating the request:", error);
      toast.error("An error occurred while updating.");
    } finally {
      setSubmit(false);
    }
  };

  const handleCancelConfirm = async () => {
    const row = wfhRows[dateToCancel];
    if (!row) return;

    setCancelling(true);
    try {
      const response = await PostServiceCaller(
        `${API_URL}work-requests/bulk-update`,
        { requests: [{ id: row.id, request_type: "Cancel", reason: row.reason || "Cancelled by user" }] },
        "put"
      );

      if (response.status === 200) {
        toast.success(`Request for ${dateToCancel} cancelled successfully!`);
        
        const remainingActive = Object.keys(wfhRows).filter(d => wfhRows[d].status !== "Cancelled").length;
        if (remainingActive <= 1) {
          onSuccess();
          onHide();
        } else {
          setWfhRows({ ...wfhRows, [dateToCancel]: { ...row, status: "Cancelled" } });
          onSuccess();
        }
      } else {
        toast.error("Failed to cancel request.");
      }
    } catch (error) {
      console.error("Error cancelling the request:", error);
      toast.error("An error occurred while cancelling.");
    } finally {
      setCancelling(false);
      setShowConfirm(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit WFH/BT Request</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <Table bordered responsive>
            <thead className="text-center" id="thcolor">
              <tr>
                <th>Date</th>
                <th>Type<span className="required">*</span></th>
                <th>Reason/Remark<span className="required">*</span></th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {Object.keys(wfhRows).length === 0 ? (
                <tr><td colSpan="3" className="text-center">No dates found in this request</td></tr>
              ) : (
                Object.keys(wfhRows).map((date) => (
                  <tr key={date}>
                    <td className="text-center align-middle fw-bold">{date}</td>
                    <td className="align-middle">
                      <select
                        className="form-select form-control"
                        value={wfhRows[date]?.type || ""}
                        onChange={(e) =>
                          setWfhRows({
                            ...wfhRows,
                            [date]: { ...wfhRows[date], type: e.target.value },
                          })
                        }
                        disabled={wfhRows[date]?.status === "Cancelled"}
                      >
                        <option value="">Select Type</option>
                        <option value="WFH">WFH</option>
                        <option value="Business Travel">Business Travel</option>
                      </select>
                    </td>
                    <td className="align-middle">
                      <textarea
                        className="form-control"
                        value={wfhRows[date]?.reason || ""}
                        onChange={(e) =>
                          setWfhRows({
                            ...wfhRows,
                            [date]: { ...wfhRows[date], reason: e.target.value },
                          })
                        }
                        rows="1"
                        disabled={wfhRows[date]?.status === "Cancelled"}
                      />
                    </td>
                    <td className="text-center align-middle">
                      {wfhRows[date]?.status}
                    </td>
                    <td className="text-center align-middle">
                      {wfhRows[date]?.status !== "Cancelled" && (
                        <button
                          type="button"
                          className="btn btn-sm btn-danger py-0 px-2"
                          title="Cancel Request"
                          onClick={() => {
                            setDateToCancel(date);
                            setShowConfirm(true);
                          }}
                        >
                          <i className="fa fa-close"></i>
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={onHide}>
            Cancel
          </Button>
          <button type="submit" className="btn btn-primary btn-sm" id="Btncolor" disabled={submitted}>
            {submitted ? <FaSpinner className="spinner" size={14} /> : "Submit"}
          </button>
        </Modal.Footer>
      </form>

      {/* Confirmation Modal */}
      <Modal show={showConfirm} onHide={() => setShowConfirm(false)} size="sm" centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5">Confirm</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to cancel this request?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowConfirm(false)} disabled={cancelling}>
            No
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={handleCancelConfirm}
            disabled={cancelling}
          >
            {cancelling ? <FaSpinner className="spinner" size={14} /> : "Yes"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Modal>
  );
};

export default EditWfhModal;
